import { AppSupabaseClient, AuthProvider, Table } from "@/types"

import { ROOT_DOMAIN, PROTOCOL, DEMO_ACCOUNT_IDS, targetingOptionsDefault } from "@/constants"
import { User } from "@supabase/supabase-js"
import { errors } from "./errors"
import { Tables } from "@/lib/database.types"
import { TiktokProfileProps } from "@shared-libs/tiktok"
import { InstagramProfileProps } from "@shared-libs/instagram"
import { Notifications } from "@/shared/libs/logsnag"

/**
 * Check if user has Google OAuth linked (queries database directly)
 * Used to enforce Google-only authentication for admin accounts
 * This queries auth.identities directly for reliability, as session data may not include identities
 */
export async function hasGoogleAuthProvider(supabase: AppSupabaseClient, user: User): Promise<boolean> {
    // Use type assertion since the RPC function may not be in generated types yet
    const { data, error } = await (supabase.rpc as any)('check_user_has_google_auth', { user_id: user.id })

    if (error) {
        console.error('Error checking Google auth provider:', error)
        return false
    }

    return data === true
}

export const getActiveProductsWithPrices = async (supabase: AppSupabaseClient) => {
    const { data, error } = await supabase.from("products").select("*, prices(*)").eq("active", true).eq("prices.active", true).order("unit_amount", { foreignTable: "prices" })

    if (error) {
        errors.add(error.message)
        throw error
    }

    return data || []
}

export const updateUserName = async (supabase: AppSupabaseClient, user: User, name: string) => {
    await supabase.from("user_profiles").update({ full_name: name }).eq("id", user.id)
}

export const getUserProfile = async (supabase: AppSupabaseClient, userId: string): Promise<Table<"user_profiles">> => {
    const { data, error } = await supabase.from("user_profiles").select("*").eq("id", userId).limit(1)

    // console.log('🚀 ~ file: supabase-queries.ts - getUserProfile() ~ data', data, 'userId:', userId)
    if (error) {
        console.error(error)
        errors.add(error.message)
        throw error
    }
    return data ? data[0] : null
}

export const getUserByCustomerId = async (supabase: AppSupabaseClient, customerId: string): Promise<Table<"user_profiles"> | null> => {
    const { data, error } = await supabase.from("user_profiles").select("*").eq("customer_id", customerId).limit(1).maybeSingle()

    if (error) {
        errors.add(error.message)
        throw error
    }

    return data
}

export const updateUserProfileNameAndAvatar = async (
    supabase: AppSupabaseClient,
    userId: string,
    {
        fullName,
        avatarUrl,
        email
    }: {
        fullName?: string
        avatarUrl?: string
        email?: string
    }
) => {
    // Build update object with only defined values
    const updateData: { full_name?: string; avatar_url?: string; email?: string } = {}
    if (fullName !== undefined) updateData.full_name = fullName
    if (avatarUrl !== undefined) updateData.avatar_url = avatarUrl
    if (email !== undefined) updateData.email = email

    // If nothing to update, return early
    if (Object.keys(updateData).length === 0) {
        return null
    }

    const { data, error } = await supabase.from("user_profiles").update(updateData).eq("id", userId).select().maybeSingle()

    if (error) {
        errors.add(error.message)
        throw error
    }

    return data
}

export const updateUserProfile = async (
    supabase: AppSupabaseClient,
    userId: string,
    data: {
        full_name?: string
        avatar_url?: string
        email?: string
        traffic_source?: string
        enabled?: boolean
        customer_id?: string
        signup_meta_tracked?: boolean
    }
) => {
    const { data: updatedData, error } = await supabase
        .from("user_profiles")
        .update({
            ...(data.full_name && { full_name: data.full_name }),
            ...(data.avatar_url && { avatar_url: data.avatar_url }),
            ...(data.email && { email: data.email }),
            ...(data.traffic_source && { traffic_source: data.traffic_source }),
            ...(data.enabled !== undefined && { enabled: data.enabled }),
            ...(data.customer_id && { customer_id: data.customer_id }),
            ...(data.signup_meta_tracked !== undefined && { signup_meta_tracked: data.signup_meta_tracked })
        })
        .eq("id", userId)
        .select()
        .maybeSingle()

    if (error) {
        errors.add(error.message)
        throw error
    }

    return updatedData
}

/* ==================== */
/* Accounts */
/* ==================== */

export const createAccount = async (
    supabase: AppSupabaseClient, 
    user: User, 
    fetched_profile_data: TiktokProfileProps | InstagramProfileProps, 
    accountPlatform: "instagram" | "tiktok", 
    themeColor?: string,
    downloadedAvatarUrl?: string | null,
    requestUsername?: string
) => {
    console.log("🚀 ~ file: supabase-queries.ts:93 ~ user", user)
    console.log("🚀 ~ file: supabase-queries.ts:94 ~ fetched_profile_data", fetched_profile_data)

    const isTiktok = (data: TiktokProfileProps | InstagramProfileProps): data is TiktokProfileProps => accountPlatform === "tiktok"

    if (!fetched_profile_data) throw new Error("No profile data found")

    // Extract username from profile data, with fallback to the request username
    // TikTok API can sometimes return null/undefined for unique_id on certain accounts
    const profileUsername = isTiktok(fetched_profile_data) ? fetched_profile_data.username : fetched_profile_data.graphql.user.username
    const username = (profileUsername?.trim() || requestUsername?.trim()) || null

    if (!username) {
        throw new Error(`Unable to determine username from ${accountPlatform} profile data`)
    }

    // Check if account already exists for this user (prevents duplicates)
    const { data: existingAccount } = await supabase
        .from("accounts")
        .select("id, username")
        .eq("user_id", user.id)
        .eq("username", username)
        .eq("platform", accountPlatform)
        .maybeSingle()

    if (existingAccount) {
        console.log(`[createAccount] Account already exists for user ${user.id}: ${username} (${accountPlatform})`)
        return existingAccount  // Return existing instead of throwing
    }
    
    // Use downloaded avatar URL if provided, otherwise construct expected Supabase URL
    const avatarUrl = downloadedAvatarUrl || `${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/avatars/${username}.jpg`

    const db_account: Partial<Tables<"accounts">> = {
        username: username,
        platform: accountPlatform,
        account_type: "trial",
        name: isTiktok(fetched_profile_data) ? fetched_profile_data.full_name : fetched_profile_data.graphql.user.full_name,
        platform_id: isTiktok(fetched_profile_data) ? fetched_profile_data.id : fetched_profile_data.graphql.user.id,
        followers: isTiktok(fetched_profile_data) ? fetched_profile_data.follower_count : (fetched_profile_data.graphql?.user?.edge_followed_by?.count ?? fetched_profile_data.graphql?.user?.follower_count ?? 0),
        starting_followers: isTiktok(fetched_profile_data) ? fetched_profile_data.follower_count : (fetched_profile_data.graphql?.user?.edge_followed_by?.count ?? fetched_profile_data.graphql?.user?.follower_count ?? 0),
        followings: isTiktok(fetched_profile_data) ? fetched_profile_data.following_count : (fetched_profile_data.graphql?.user?.edge_follow?.count ?? fetched_profile_data.graphql?.user?.following_count ?? 0),
        ...(isTiktok(fetched_profile_data) && {
            likes: fetched_profile_data.likes_count,
            starting_likes: fetched_profile_data.likes_count,
            medias: fetched_profile_data.media_count
        }),
        // Instagram medias - try direct field first, then edge_owner_to_timeline_media.count fallback
        ...(!isTiktok(fetched_profile_data) && {
            medias: fetched_profile_data.graphql?.user?.media_count ?? 
                (fetched_profile_data.graphql?.user?.edge_owner_to_timeline_media as any)?.count ?? null
        }),
        conversions: 0,
        actions: 0,
        user_id: user.id,
        active: false,
        connected: true,
        avatar_url: avatarUrl,
        targeting_options: targetingOptionsDefault,
        created_at: new Date().toISOString(),
        ...(themeColor && { theme_color: themeColor as "Default" | "Purple" | "Indigo" | "Green" | "Amber" | "Pink" })
    }

    // Insert account in db
    const { data, error } = await supabase
        .from("accounts")
        .insert(db_account as Tables<"accounts">)
        .select("id")
        .single()

    if (error) {
        console.error(error)
        console.error(error.code)

        if (error.code == "23505") {
            const error = new Error(`This ${accountPlatform === "instagram" ? "Instagram" : "TikTok"} profile is already connected to an Outfame account.`)
            errors.add(error.message)
            throw error
        } else errors.add(error.message)

        throw error
    }

    // Send LogSnag notification for new account connected. The LogSnag token is server-only,
    // so the browser asks the server to send it (same alert, verified session).
    if (typeof window !== "undefined") {
        fetch("/api/notifications/account-connected", {
            method: "POST",
            credentials: "include",
            keepalive: true,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username, platform: accountPlatform })
        }).catch(() => {})
    } else {
        Notifications.sendNotification(
            "accounts",
            "New Account Connected",
            "🚀",
            true,
            user.id,
            {
                username: username,
                platform: accountPlatform,
                followers: db_account.followers?.toString() || "0",
                email: user.email || "unknown"
            }
        )
    }

    return data
}

/* ==================== */
/* Accounts */
/* ==================== */

export async function getUserHasAccounts(supabaseClient: AppSupabaseClient, userId: string): Promise<boolean> {
    const { data: userHasAccounts, error } = await supabaseClient
        .rpc("check_if_user_has_accounts", {
            user_id: userId
        })
        .single()
    if (error) {
        console.error(error)
        throw error
    }
    return Boolean(userHasAccounts)
}

export async function getUserHasUpgradableAccounts(supabaseClient: AppSupabaseClient, userId: string): Promise<boolean> {
    const { data: userHasAccounts, error } = await supabaseClient
        .rpc("check_if_user_has_upgradable_accounts", {
            user_id: userId
        })
        .single()
    if (error) {
        console.error(error)
        throw error
    }
    return Boolean(userHasAccounts)
}

export const getAllAccountsForUser = async (supabase: AppSupabaseClient) => {
    // Order by created_at DESC so most recently connected accounts appear first
    // This ensures consistent ordering across all account lists and switchers
    const { data, error } = await supabase
        .from("accounts")
        .select("*")
        .neq("account_type", "archived")
        .order("created_at", { ascending: false })

    if (error) {
        errors.add(error.message)
        throw error
    }

    return data || []
}

export const getAccountById = async (supabase: AppSupabaseClient, accountId: string) => {
    // Guard against empty/invalid accountId to prevent UUID parsing errors
    if (!accountId || accountId.trim() === "") {
        return null
    }

    const { data, error } = await supabase.from("accounts").select("*").eq("id", accountId).single()

    if (error) {
        errors.add(error.message)
        throw error
    }
    return data
}

export const getAccountBySubscriptionId = async (supabase: AppSupabaseClient, subscription_id: string): Promise<Table<"accounts"> | null> => {
    const { data, error } = await supabase.from("accounts").select("*").eq("subscription_id", subscription_id).limit(1).maybeSingle()

    if (error) {
        errors.add(error.message)
        throw error
    }
    return data
}

export const getAccountByCustomerId = async (supabase: AppSupabaseClient, customer_id: string): Promise<Table<"accounts"> | null> => {
    const { data, error } = await supabase.from("accounts").select("*").eq("customer_id", customer_id).limit(1).maybeSingle()

    if (error) {
        errors.add(error.message)
        throw error
    }
    return data
}

export const getAccountByUsername = async (supabase: AppSupabaseClient, accountUsername: string): Promise<Table<"accounts">> => {
    const { data, error } = await supabase.from("accounts").select("*").eq("username", accountUsername).single()
    if (error) {
        errors.add(error.message)
        throw error
    }
    return data
}

export const getAllActivitiesForAccount = async (supabase: AppSupabaseClient, accountId: string, page: number = 0) => {
    const { data, error } = await supabase
        .from("activities")
        .select("id,created_at,account_id,type,target_id,subject_name,count,thumbnails,targets(type,name)")
        .eq("account_id", accountId)
        .order("created_at", { ascending: false })
        .range(page * 10, (page + 1) * 10 - 1)

    if (error) {
        errors.add(error.message)
        throw error
    }

    // Loop through activities and if activity.thumbnails is JSON string, parse it
    data.forEach((activity: any) => {
        if (activity.thumbnails && typeof activity.thumbnails === "string") {
            activity.thumbnails = JSON.parse(activity.thumbnails)
        }
    })

    return data
}

export const getAllUpdatesForAccount = async (supabase: AppSupabaseClient, accountId: string, fetchAllUpdates: boolean = false) => {
    let query = supabase.from("updates").select("*").eq("account_id", accountId).order("created_at", { ascending: true })

    if (!fetchAllUpdates) {
        const thirtyDaysAgo = new Date()
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
        query = query.gte("created_at", thirtyDaysAgo.toISOString())
    } else {
        query = query.limit(730)
    }

    const { data, error } = await query

    if (error) {
        errors.add(error.message)
        throw error
    }
    return data
}

export const getAllTargetsForAccount = async (supabase: AppSupabaseClient, accountId: string) => {
    const { data, error } = await supabase.from("targets").select("id, account_id, target, name, type, platform, target_thumbnail, enabled, count, source, created_at").eq("enabled", true).eq("account_id", accountId)

    console.log("getAllTargetsForAccount", data)

    if (error) {
        errors.add(error.message)
        throw error
    }
    return data
}

export const addTargetForAccount = async (supabase: AppSupabaseClient, accountId: string, target: any) => {
    try {
        // Download and upload avatar to Supabase storage for user targets
        let downloadedAvatarUrl
        if (target.type == "user" && target.thumbnail) {
            try {
                const response = await fetch(`/api/${target.platform}/download-avatar`, {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        username: target.name,
                        avatar_url: target.thumbnail
                    })
                })

                if (response.ok) {
                    const data = await response.json()
                    downloadedAvatarUrl = data.avatar_url
                    console.log(`[addTarget] Avatar uploaded to: ${downloadedAvatarUrl}`)
                } else {
                    console.error(`[addTarget] Avatar download failed: ${response.status}`)
                }
            } catch (error) {
                console.error("[addTarget] Error downloading avatar:", error)
            }
        }

        // If it's a demo account, don't actually add to Supabase
        if (DEMO_ACCOUNT_IDS.includes(accountId)) {
            return {
                account_id: accountId,
                ...target,
                ...(downloadedAvatarUrl && { thumbnail: downloadedAvatarUrl }),
                id: Math.random().toString(36).substring(7),
                created_at: new Date().toISOString()
            }
        }

        // Check if target already exists (enabled or disabled) for this account
        if (target.platform_id) {
            const { data: existingTarget } = await supabase.from("targets").select("*").eq("account_id", accountId).eq("platform_id", target.platform_id).single()

            if (existingTarget) {
                // Target exists - if disabled, re-enable it; if enabled, throw duplicate error
                if (!existingTarget.enabled) {
                    // Re-enable the existing target and update its data
                    const targetName = target.name || target.target
                    const { data: updatedTarget, error: updateError } = await supabase
                        .from("targets")
                        .update({
                            enabled: true,
                            name: targetName,
                            target: targetName,
                            target_thumbnail: downloadedAvatarUrl || target.target_thumbnail || existingTarget.target_thumbnail
                        })
                        .eq("id", existingTarget.id)
                        .select("*")
                        .single()

                    if (updateError) throw updateError
                    return { account_id: accountId, ...updatedTarget }
                } else {
                    // Target already exists and is enabled - throw a specific error
                    throw { code: "23505", message: `Target ${target.name} already exists for this account.` }
                }
            }
        }

        // Insert new target (don't spread target.id if it somehow exists)
        const { id: _ignoredId, ...targetWithoutId } = target
        const { data, error } = await supabase
            .from("targets")
            .insert({
                ...targetWithoutId,
                account_id: accountId,
                // Ensure both name and target are always set (generate_activity uses name)
                name: targetWithoutId.name || targetWithoutId.target,
                target: targetWithoutId.target || targetWithoutId.name,
                ...(downloadedAvatarUrl && { thumbnail: downloadedAvatarUrl })
            })
            .select("*")
            .single()

        if (error) throw error

        return { account_id: accountId, ...data }
    } catch (error) {
        console.error("Error adding target for account:", error)
        throw error
    }
}

export const removeTargetForAccount = async (supabase: AppSupabaseClient, accountId: string, targetId: string) => {
    // If it's a demo account, don't actually remove from Supabase
    if (DEMO_ACCOUNT_IDS.includes(accountId)) {
        return {
            account_id: accountId,
            targetId: targetId
        }
    }

    const { data, error } = await supabase.from("targets").update({ enabled: false }).eq("id", parseInt(targetId)).single()

    console.log("removeTargetForAccount", data)

    if (error) {
        errors.add(error.message)
        throw error
    }
    return { account_id: accountId, targetId: targetId }
}

export const getLast30DaysUpdatesForAccount = async (supabase: AppSupabaseClient, accountId: string) => {
    const { data, error } = await supabase
        .from("updates")
        .select("*")
        .eq("account_id", accountId)
        .gte("created_at", new Date(new Date().setDate(new Date().getDate() - 30)))
        .single()

    if (error) {
        errors.add(error.message)
        throw error
    }
    return data
}

export const updateAccount = async (supabase: AppSupabaseClient, accountId: string, updatedData: Partial<Table<"accounts">>): Promise<Table<"accounts">> => {
    const { data, error } = await supabase.from("accounts").update(updatedData).eq("id", accountId).select("*").single()

    if (error) {
        errors.add(error.message)
        throw error
    }

    return data
}

/* ==================== */
/* Organizations */
/* ==================== */

export const getAllOrganizationsForUser = async (supabase: AppSupabaseClient) => {
    const { data, error } = await supabase.from("organizations").select("*, organization_team_members(id,member_id,member_role, user_profiles(*)), subscriptions(id, prices(id,products(id,name)))")
    if (error) {
        errors.add(error.message)
        throw error
    }

    return data || []
}

export const getOrganizationById = async (supabase: AppSupabaseClient, organizationId: string) => {
    const { data, error } = await supabase
        .from("organizations")
        // query team_members and team_invitations in one go
        .select("*")
        .eq("id", organizationId)
        .single()

    if (error) {
        errors.add(error.message)
        throw error
    }

    return data
}

export const createOrganization = async (supabase: AppSupabaseClient, user: User, name: string) => {
    const { data, error } = await supabase
        .from("organizations")
        .insert({
            title: name,
            created_by: user.id
        })
        .select("*")
        .single()

    if (error) {
        errors.add(error.message)
        throw error
    }

    return data
}

export const updateOrganizationTitle = async (supabase: AppSupabaseClient, organizationId: string, title: string): Promise<Table<"organizations">> => {
    const { data, error } = await supabase
        .from("organizations")
        .update({
            title
        })
        .eq("id", organizationId)
        .select("*")
        .single()

    if (error) {
        errors.add(error.message)
        throw error
    }

    return data
}

export const getTeamMembersInOrganization = async (supabase: AppSupabaseClient, organizationId: string) => {
    const { data, error } = await supabase.from("organization_team_members").select("*, user_profiles(*)").eq("organization_id", organizationId)

    if (error) {
        errors.add(error.message)
        throw error
    }

    return data || []
}

export const getPendingTeamInvitationsInOrganization = async (supabase: AppSupabaseClient, organizationId: string) => {
    const { data, error } = await supabase.from("organization_team_invitations").select("*, inviter:user_profiles!inviter_user_id(*), invitee:user_profiles!invitee_user_id(*)").eq("organization_id", organizationId).eq("status", "active")

    if (error) {
        errors.add(error.message)
        throw error
    }

    return data || []
}

export const getOrganizationSubscription = async (supabase: AppSupabaseClient, organizationId: string) => {
    // @ts-ignore - Deep type instantiation
    const { data, error } = await supabase.from("subscriptions").select("*, prices(*, products(*))").eq("organization_id", organizationId).in("status", ["trialing", "active"]).single()

    if (error) {
        errors.add(error.message)
        throw error
    }

    return data
}

export const getUserOrganizationRole = async (supabase: AppSupabaseClient, userId: string, organizationId: string) => {
    const { data, error } = await supabase.from("organization_team_members").select("member_id, member_role").eq("member_id", userId).eq("organization_id", organizationId).single()

    if (error) {
        errors.add(error.message)
        throw error
    }

    return data
}

/* ==================== */
/* Admin */
/* ==================== */

export async function getIsAppAdmin(supabaseClient: AppSupabaseClient, authUser: User): Promise<boolean> {
    const { data: isUserAppAdmin, error } = await supabaseClient
        .rpc("check_if_user_is_app_admin", {
            user_id: authUser.id
        })
        .single()
    if (error) {
        throw error
    }

    return Boolean(isUserAppAdmin)
}

/* ==================== */
/* AUTH */
/* ==================== */

export const signInWithMagicLink = async (supabase: AppSupabaseClient, email: string) => {
    const protocol = process.env.NODE_ENV === "production" ? "https://" : "http://"
    const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
            emailRedirectTo: `${protocol}${ROOT_DOMAIN}/auth/callback`
        }
    })

    if (error) {
        errors.add(error.message)
        throw error
    }
}

export const signInWithPassword = async (supabase: AppSupabaseClient, email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
        email,
        password
    })

    if (error) {
        errors.add(error.message)
        throw error
    }
}

export const resetPassword = async (supabase: AppSupabaseClient, email: string) => {
    const protocol = process.env.NODE_ENV === "production" ? "https://" : "http://"
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${protocol}${ROOT_DOMAIN}/auth/confirm?type=recovery&next=/reset-password`
    })

    if (error) {
        errors.add(error.message)
        throw error
    }
}

export const updatePassword = async (supabase: AppSupabaseClient, password: string) => {
    const { error } = await supabase.auth.updateUser({
        password
    })

    if (error) {
        errors.add(error.message)
        throw error
    }
}

/**
 * Sign in with OAuth provider (Google, etc.)
 *
 * AUTH ROUTES ARCHITECTURE:
 * - /auth/callback → OAuth flows (uses exchangeCodeForSession)
 * - /auth/confirm  → Email flows (uses verifyOtp) - magic links, password reset, email confirmation
 *
 * OAuth flow:
 * 1. User clicks "Sign in with Google"
 * 2. Supabase redirects to Google OAuth
 * 3. Google redirects back to /auth/callback?code=xxx&provider=google
 * 4. /auth/callback exchanges code for session via exchangeCodeForSession()
 * 5. If new user, tracking data is captured and LogSnag notification sent
 */
export const signInWithProvider = async (supabase: AppSupabaseClient, provider: AuthProvider) => {
    const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
            // IMPORTANT: OAuth MUST use /auth/callback (not /auth/confirm)
            // - /auth/callback uses exchangeCodeForSession() for OAuth code exchange
            // - /auth/confirm uses verifyOtp() for email-based token verification
            // The provider param allows the callback to record which OAuth provider was used
            redirectTo: `https://www.outfame.com/auth/callback?provider=${provider}`
        }
    })

    if (error) {
        errors.add(error.message)
        throw error
    }
}

/**
 * Sign up with email and password
 *
 * CURRENT CONFIGURATION: Supabase autoconfirm is ENABLED
 * - Users are auto-confirmed immediately (no email verification required)
 * - email_confirmed_at is set within ~8ms of created_at
 * - emailRedirectTo is NOT used (no confirmation email sent)
 *
 * IF AUTOCONFIRM IS DISABLED IN THE FUTURE:
 * - Supabase will send a confirmation email
 * - User must click link to verify email
 * - Link goes to /auth/confirm (NOT /auth/callback)
 * - /auth/confirm uses verifyOtp({ type: "email", token_hash }) to confirm
 *
 * TRACKING DATA FLOW:
 * 1. signUp() creates user (auto-confirmed)
 * 2. Client reads attribution cookies (gclid, fbclid, utm_*, etc.)
 * 3. Client calls /api/tracking/refresh to capture geo data + update user_profiles
 * 4. LogSnag notification sent with traffic_source
 *
 * NOTIFICATION SOURCES:
 * - Email signup: "signup_form" (this function)
 * - OAuth signup: "oauth_callback" (/auth/callback route)
 * - Setup flow:   "setup_flow" (/api/accounts/setup)
 */
export const signUp = async (supabase: AppSupabaseClient, email: string, password: string) => {
    if (password.length > 256) {
        throw new Error("Password exceeds maximum length of 256 characters")
    }

    console.log(`[SignUp] Creating user with email: ${email}`)
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
            // IMPORTANT: Email verification links MUST use /auth/confirm (not /auth/callback)
            // - /auth/confirm uses verifyOtp() for email token verification
            // - /auth/callback uses exchangeCodeForSession() for OAuth only
            // Currently unused because autoconfirm is enabled, but kept correct for future-proofing
            emailRedirectTo: `${PROTOCOL}://${ROOT_DOMAIN}/auth/confirm`
        }
    })

    if (signUpError) {
        throw signUpError
    }

    console.log("[SignUp] Result:", signUpData?.user?.id || "no user")

    // Capture tracking data via API call (server-side will get Cloudflare headers for geo)
    if (signUpData?.user) {
        // Read attribution cookies from browser
        // These are set by TrackingScripts.tsx when user lands with gclid/fbclid/utm_* params
        const getCookie = (name: string) => {
            if (typeof document === "undefined") return null
            const match = document.cookie.match(new RegExp(`${name}=([^;]+)`))
            return match?.[1] || null
        }

        const googleAdsCtx: Record<string, string> = {}
        // Full list of attribution cookies - must match TrackingScripts.tsx
        const cookieParams = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_campaignid", "utm_adgroupid", "gclid", "gclid_raw", "gbraid", "wbraid", "fbclid", "ttclid", "ref"]
        cookieParams.forEach((param) => {
            const value = getCookie(param)
            if (value) googleAdsCtx[param] = value
        })

        // Derive traffic source using same logic as server-side (deriveTrafficSource)
        const trafficSource = googleAdsCtx.gclid
            ? "google_ads"
            : googleAdsCtx.fbclid
              ? "meta_ads"
              : googleAdsCtx.ttclid
                ? "tiktok_ads"
                : googleAdsCtx.utm_source || "organic"

        try {
            // Server-side API captures geo from Cloudflare headers + updates user_profiles
            // This is the primary tracking mechanism for email signups
            await fetch("/api/tracking/refresh", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ googleAdsCtx, email, isNewSignup: true })
            })
        } catch (e) {
            // Non-blocking
        }
    }

    return signUpData
}
